import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';

import { CustomTextConfigurationApi } from 'Api/CustomTextConfiguration/CustomTextConfigurationApi';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_SAVE } from 'Config/Icons';
import { CONFIGURATION } from 'Config/Paths';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { CustomTextConfiguration } from 'Models/CustomTextConfiguration';
import { DEFAULT_TPRM_INTAKE_INSTRUCTIONS } from 'Models/TPRM';
import { DEFAULT_UCRM_INTAKE_INSTRUCTIONS } from 'Models/Ucrm';

import styles from './ConfigureCustomizedText.module.css';

const PAGE_TILE = 'Customize Text';

export interface ConfigureCustomizedTextProps {
    customTextConfigurationApi: CustomTextConfigurationApi;
}

/**
 * Renders a series of text fields where the user can set customized text to override SummIT's defaults in various places of the app.
 * This is intended to be expanded in the future, but currently the only customizations that can be made are to the TPRM and UCRM intake instructions.
 */
export const ConfigureCustomizedText = (props: ConfigureCustomizedTextProps) => {
    const cachedData = useCachedData();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);
    const [requestIsInProgress, setRequestIsInProgress] = useState(false);
    const [toastContent, setToastContent] = useState<{ variant: 'success' | 'failure'; text: string }>();
    const [tprmIntakeInstructions, setTprmIntakeInstructions] = useState<string>();
    const [ucrmIntakeInstructions, setUcrmIntakeInstructions] = useState<string>();

    useEffect(() => {
        const getConfiguration = async () => {
            try {
                const configuration = (await props.customTextConfigurationApi.getCustomizedText()).data;
                setTprmIntakeInstructions(configuration.tprm_intake_instructions ?? DEFAULT_TPRM_INTAKE_INSTRUCTIONS);
                setUcrmIntakeInstructions(configuration.ucrm_intake_instructions ?? DEFAULT_UCRM_INTAKE_INSTRUCTIONS);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };
        getConfiguration();
    }, [props.customTextConfigurationApi]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (tprmIntakeInstructions === undefined || ucrmIntakeInstructions === undefined) {
        return <Placeholder />;
    }

    /** A utility function for easily connecting text changes in a text area to specific state. Intended to be used widely as more custom text configuration becomes available to the user. */
    const handleTextChange = (setState: (value: string) => void) => (event: React.FormEvent<HTMLInputElement>) => setState(event.currentTarget.value);

    const submit = async () => {
        try {
            setRequestIsInProgress(true);
            const configuration: CustomTextConfiguration = {
                tprm_intake_instructions: processTextForRequest(tprmIntakeInstructions, DEFAULT_TPRM_INTAKE_INSTRUCTIONS),
                ucrm_intake_instructions: processTextForRequest(ucrmIntakeInstructions, DEFAULT_UCRM_INTAKE_INSTRUCTIONS),
            };
            await props.customTextConfigurationApi.setCustomizedText(configuration);
            setToastContent({ variant: 'success', text: 'Configuration saved.' });
        } catch (error) {
            console.log(error.message);
            setToastContent({ variant: 'failure', text: GENERIC_ERROR_MESSAGE });
        } finally {
            setRequestIsInProgress(false);
        }
    };

    return (
        <>
            {toastContent && <TextToast variant={toastContent.variant} clearToast={() => setToastContent(undefined)} text={toastContent.text} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${CONFIGURATION}`}>Settings</BreadcrumbLink>
                        <BreadcrumbText>{PAGE_TILE}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={PAGE_TILE}
                headerDescription="Customize explanatory text that members of your organization will see during various workflows."
                body={[
                    {
                        content: (
                            <Box component="form">
                                <fieldset disabled={requestIsInProgress}>
                                    <div className={styles.customTextContainer}>
                                        <Text noStyles variant="Header3">
                                            Instructions to create a third party and/or third-party service (Third-Party Risk Management)
                                        </Text>
                                        <Text variant="Text4">
                                            Displayed on the <i>Create</i> page in the <i>Third-Party Risk Management</i> module. If blank, SummIT's default text will be used.
                                        </Text>
                                        <FormFieldTextArea handleChange={handleTextChange(setTprmIntakeInstructions)} value={tprmIntakeInstructions} formFieldId="tprmIntakePageHeaderText" placeholder={tprmIntakeInstructions === '' ? DEFAULT_TPRM_INTAKE_INSTRUCTIONS : undefined} />
                                    </div>
                                    {cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]) && (
                                        <div className={styles.customTextContainer}>
                                            <Text noStyles variant="Header3">
                                                Instructions to create a business and/or use case (AI Governance)
                                            </Text>
                                            <Text variant="Text4">
                                                Displayed on the <i>Create</i> page in the <i>AI Governance</i> module. If blank, SummIT's default text will be used.
                                            </Text>
                                            <FormFieldTextArea handleChange={handleTextChange(setUcrmIntakeInstructions)} value={ucrmIntakeInstructions} formFieldId="ucrmIntakePageHeaderText" placeholder={ucrmIntakeInstructions === '' ? DEFAULT_UCRM_INTAKE_INSTRUCTIONS : undefined} />
                                        </div>
                                    )}
                                    <div className={styles.saveButtonContainer}>
                                        <Button variant="primary" isLoading={requestIsInProgress} loadingText="Saving..." onClick={submit} fontAwesomeImage={ICON_SAVE}>
                                            Save
                                        </Button>
                                    </div>
                                </fieldset>
                            </Box>
                        ),
                    },
                ]}
            />
        </>
    );
};

/**
 * A utility function for preventing empty text, and text identical to SummIT's default text, from being included in requests.
 * Default text is excluded because fields are pre-filled with it when no custom text exists; saving it would store a copy of the default that would not pick up future changes to the default (and would store text for features the Client does not have, since their fields are hidden but still pre-filled).
 */
const processTextForRequest = (text: string, defaultText: string): string | undefined => {
    const trimmedText = text.trim();
    return trimmedText === '' || trimmedText === defaultText.trim() ? undefined : trimmedText;
};
